/**
 * The document as it appears in the transcript.
 *
 * A card rather than the document itself. A board pack inline in a chat bubble
 * is unreadable and pushes the rest of the conversation off screen, so the
 * transcript carries a reference to it and the document opens on its own
 * surface. This is also what keeps a revision legible: two cards in sequence
 * read as two decisions, where two full documents read as a mistake.
 */
import { AlertTriangle, FileText } from "lucide-react";
import type { ArtifactComposedEvent } from "../../services/agui";

interface ArtifactCardProps {
  event: ArtifactComposedEvent;
  onOpen: () => void;
}

export function ArtifactCard({ event, onOpen }: ArtifactCardProps) {
  const sections = event.spec.sections?.length ?? 0;

  if (!event.saved) {
    return (
      <div className="inline-notice warning" role="alert">
        <AlertTriangle size={17} aria-hidden="true" />
        <span>
          {event.saveError ??
            "The document was composed but could not be saved to your workspace."}{" "}
          It can still be read, but not issued or revised.
        </span>
        <button type="button" className="button tertiary" onClick={onOpen}>
          Read it anyway
        </button>
      </div>
    );
  }

  return (
    <div className="artifact-card">
      <span className="artifact-card-icon" aria-hidden="true">
        <FileText size={18} />
      </span>
      <div className="artifact-card-body">
        <strong>{event.title}</strong>
        <small>
          {/* Version is named on the card, not only inside the document. Once a
              document has been sent, which one someone is looking at is the
              first thing they need to know. */}
          Document · {sections} section{sections === 1 ? "" : "s"} · v
          {event.version}
          {event.revisionOf ? " · revised" : ""}
        </small>
      </div>
      <button type="button" className="button secondary" onClick={onOpen}>
        View
      </button>
    </div>
  );
}

export default ArtifactCard;
